import { useQuery } from '@apollo/client';
import { useAuthState } from '@application/state';
import { graphql } from '@gql/gql';
import type {
  MtNotificationsGetNotificationsQuery,
  MtNotificationsGetNotificationsQueryVariables,
} from '@gql/graphql';
import { SortOrder } from '@gql/graphql';
import { useMemo } from 'react';
import { PAGE_SIZE } from './constants';

export const MT_NOTIFICATIONS_GET_NOTIFICATIONS = graphql(`
  query MtNotificationsGetNotifications(
    $take: Int
    $skip: Int
    $where: NotificationWhereInput
    $orderBy: NotificationOrderByWithRelationAndSearchRelevanceInput
  ) {
    findManyNotification(skip: $skip, take: $take, where: $where, orderBy: $orderBy) {
      items {
        id
        createdAt
        type
        success
        applicationId
        litterId
        account {
          id
          language
        }
        breeder {
          id
          displayName
          translations {
            language
            url
          }
        }
        application {
          dogBreeder {
            displayName
          }
          account {
            name
            firstName
            lastName
            language
          }
          breedPreference {
            translations {
              name
              language
            }
          }
        }
      }
      count
      unreadMessagesCount
    }
  }
`);

export const useGetNotifications = () => {
  const variables = useMemo((): MtNotificationsGetNotificationsQueryVariables => {
    return {
      where: {},
      orderBy: { createdAt: SortOrder.desc },
      skip: 0,
      take: PAGE_SIZE,
    };
  }, []);
  const authState = useAuthState();

  const { data, loading, refetch, fetchMore } = useQuery<
    MtNotificationsGetNotificationsQuery,
    MtNotificationsGetNotificationsQueryVariables
  >(MT_NOTIFICATIONS_GET_NOTIFICATIONS, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !authState.token,
  });

  const handleRefetchNotifications = (take: number) => {
    return refetch({ take });
  };

  const handleLoadMoreNotifications = (skip: number) => {
    return fetchMore({ variables: { skip } });
  };

  return {
    notifications: data?.findManyNotification.items,
    notificationsLoading: loading,
    unreadMessagesCount: data?.findManyNotification.unreadMessagesCount,
    refetchNotifications: handleRefetchNotifications,
    canLoadMore:
      (data?.findManyNotification.items.length ?? 0) < (data?.findManyNotification.count ?? 0),
    loadMoreNotifications: handleLoadMoreNotifications,
  };
};
